import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { produce } from 'immer'
import { Fragment } from 'react'
import { twJoin } from 'tailwind-merge'
import { useMapActions } from '@/components/regionen/pageRegionSlug/hooks/mapState/useMapState'
import type { MapDataCategoryConfig } from '@/components/regionen/pageRegionSlug/hooks/useQueryState/useCategoriesConfig/type'
import { useCategoriesConfig } from '@/components/regionen/pageRegionSlug/hooks/useQueryState/useCategoriesConfig/useCategoriesConfig'
import { DisclosureChevron } from '@/components/shared/DisclosureChevron/DisclosureChevron'
import { MotionCollapse } from '@/components/shared/motion/MotionCollapse'
import { SubcategoryCheckbox } from '../Subcategories/SubcategoryCheckbox'
import { SubcategoryDropdown } from '../Subcategories/SubcategoryDropdown'
import { CategoryHeadlineToggle } from './CategoryHeadlineToggle'

type Props = { categoryConfig: MapDataCategoryConfig; active: boolean }

export const CategoryDisclosure = ({ categoryConfig: currCategoryConfig, active }: Props) => {
  const { clearInspectorFeatures } = useMapActions()
  const { categoriesConfig, setCategoriesConfig } = useCategoriesConfig()

  const selectCategory = (categoryId: string) => {
    const newConfig = produce(categoriesConfig, (draft) => {
      const category = draft.find((th) => th.id === categoryId)
      if (category) {
        category.active = !category.active
      }
    })
    void setCategoriesConfig(newConfig)
    clearInspectorFeatures()
  }

  return (
    <Disclosure
      as="div"
      key={currCategoryConfig.name}
      className="border-t border-t-gray-200 first:border-t-0"
    >
      {({ open }) => (
        <>
          <header className="flex min-w-0 justify-between">
            <CategoryHeadlineToggle
              active={active}
              open={open}
              handleChange={() => selectCategory(currCategoryConfig.id)}
            >
              <h2
                className={twJoin(
                  'w-full font-semibold',
                  open ? '' : 'overflow-hidden text-ellipsis whitespace-nowrap',
                )}
              >
                {currCategoryConfig.name}
              </h2>
              <p
                className={twJoin(
                  'mt-0.5 w-full text-xs leading-3 text-gray-400',
                  open ? '' : 'overflow-hidden text-ellipsis whitespace-nowrap',
                )}
                title={open ? undefined : currCategoryConfig.desc}
              >
                {currCategoryConfig.desc}
              </p>
            </CategoryHeadlineToggle>
            {/* Larger tap target + chevron on mobile (the flyout has room); compact on desktop. */}
            <DisclosureButton className="flex flex-none cursor-pointer items-center justify-center border-l border-gray-200 px-4 text-yellow-500 hover:bg-yellow-50 sm:px-1">
              <DisclosureChevron open={open} side="trailing" className="size-9 sm:size-7" />
            </DisclosureButton>
          </header>

          <MotionCollapse open={open}>
            <DisclosurePanel static as="nav" className="mt-3 mb-2 space-y-2.5">
              {currCategoryConfig.subcategories?.map((subcat) => {
                const showSpacer = currCategoryConfig.spacerAfter?.has(subcat.id)

                return (
                  <Fragment key={subcat.id}>
                    {subcat.ui === 'dropdown' ? (
                      <SubcategoryDropdown
                        categoryId={currCategoryConfig.id}
                        subcategory={subcat}
                        disabled={!active}
                      />
                    ) : (
                      <SubcategoryCheckbox
                        categoryId={currCategoryConfig.id}
                        subcategory={subcat}
                        disabled={!active}
                      />
                    )}
                    {showSpacer && <hr className="mx-2 my-2.5 border-gray-100" />}
                  </Fragment>
                )
              })}
            </DisclosurePanel>
          </MotionCollapse>
        </>
      )}
    </Disclosure>
  )
}
